import { useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { Headline } from '@xing-com/typography';

import { FormattedLink } from '../base-components';
import { FOOTER_EXTERNAL_LINKS, getFooterExternalLinks } from '../constants';
import type { CareerHubTopicTitlesQuery } from '../graphql/career-hub-topic-titles-query.gql-types';
import { CareerHubTopicTitlesDocument } from '../graphql/career-hub-topic-titles-query.gql-types';

import { ParamsTrackingMapper } from './constants';
import { RelevantLinksData } from './relevant-links-data';
import { Styled } from './relevant-links.styles';
import { SkeletonRelevantLinks } from './skeleton-relevant-links';

type RelevantLinksProps = {
  topicKey?: string;
};

export const RelevantLinks: React.FC<RelevantLinksProps> = ({ topicKey }) => {
  const { data, loading } = useQuery<CareerHubTopicTitlesQuery>(
    CareerHubTopicTitlesDocument
  );
  const location = useLocation();
  const currentUrl = location.pathname;
  const trackingKey = topicKey && ParamsTrackingMapper.get(topicKey);
  const collection =
    data?.careerHubTopics?.collection ??
    (trackingKey && currentUrl.includes(trackingKey) ? RelevantLinksData : []);
  const servicesLinkBox = topicKey
    ? getFooterExternalLinks(topicKey)
    : FOOTER_EXTERNAL_LINKS;
  const topicsLinkKey = `?sc_o=career_hub_application_other_topics_click&sc_o_PropActionOrigin=career_hub_application_${topicKey}`;

  return (
    <Styled.GridContainer variant="FullSize">
      {loading ? (
        <SkeletonRelevantLinks />
      ) : (
        <>
          <Styled.GridItem span={12} spanMedium={6}>
            <Headline size="large" as="strong">
              <FormattedMessage id="CAREER_HUB_LOGOUT_FOOTER_SERVICES" />
            </Headline>
            {servicesLinkBox.map(({ href, text }, index) => (
              <FormattedLink {...{ href, text, index }} key={text} isExternal />
            ))}
          </Styled.GridItem>
          {collection.length > 0 ? (
            <Styled.GridItem span={12} spanMedium={6}>
              <Headline
                size="large"
                data-qa="logged-out-footer-topics"
                as="strong"
              >
                <FormattedMessage id="CAREER_HUB_LOGOUT_FOOTER_TOPICS" />
              </Headline>
              {collection.map((articles, index) => (
                <FormattedLink
                  href={`/karriere-ratgeber/${articles?.key}`}
                  urlWithTracking={`/karriere-ratgeber/${articles?.key}${
                    trackingKey ? topicsLinkKey : ''
                  }`}
                  key={articles?.key}
                  text={articles?.title || ''}
                  index={index}
                />
              ))}
            </Styled.GridItem>
          ) : null}
        </>
      )}
    </Styled.GridContainer>
  );
};
